// Settings modal — surfaces the [game] monitor toggles (Phase 3 +
// Phase 4) and the [llm].disabled_pipelines list (Phase 5) in one
// place so the user can flip everything from the UI without
// hand-editing user.toml.
//
// Saves go through `set_settings` IPC which persists to user.toml
// and reloads the global config — changes take effect on the next
// round (or next monitor tick) without an app restart.

import { useEffect, useState } from "react";
import {
  getSettings,
  setSettings,
  type SettingsSnapshot,
} from "../ipc/commands";
import { useStore } from "../state/store";

interface SettingsPanelProps {
  onClose: () => void;
}

const PIPELINES_DISABLEABLE: { id: string; label: string; help: string }[] = [
  {
    id: "ai_gemini",
    label: "Gemini (pinpoint)",
    help: "Cheapest LLM model in the ensemble. Strong on plate / signage detection.",
  },
  {
    id: "ai_claude",
    label: "Claude (pinpoint)",
    help: "Most expensive slot. Strongest visual reasoning. Sometimes refuses on game UI.",
  },
  {
    id: "ai_gpt",
    label: "GPT (pinpoint)",
    help: "Independent third opinion. Decent vegetation / architecture detection.",
  },
  {
    id: "ocr",
    label: "OCR + GeoNames",
    help: "Reads place names off road signs and pins to GeoNames coords. Highest precision when text is visible — disable only if you don't trust the lookups.",
  },
  {
    id: "meta",
    label: "Meta-detector",
    help: "Surfaces region-narrowing signals (driving side, script, vegetation) without producing coords. Diagnostic only.",
  },
];

export function SettingsPanel({ onClose }: SettingsPanelProps) {
  const [settings, setLocal] = useState<SettingsSnapshot | null>(null);
  const [saving, setSaving] = useState(false);
  const showToast = useStore((s) => s.showToast);
  const setDisabledPipelines = useStore((s) => s.setDisabledPipelines);

  useEffect(() => {
    let cancelled = false;
    getSettings()
      .then((s) => {
        if (!cancelled) {
          setLocal(s);
          setDisabledPipelines(s.disabled_pipelines);
        }
      })
      .catch((e) => {
        if (cancelled) return;
        showToast(`Couldn't load settings: ${String(e)}`, "error");
      });
    return () => {
      cancelled = true;
    };
  }, [showToast, setDisabledPipelines]);

  /// Auto-save: every toggle change persists immediately to user.toml
  /// + reloads the global config + updates the UI store. Removes the
  /// "I unchecked Claude but he's still firing" surprise where the
  /// previous flow required a separate Save click.
  const update = async (next: SettingsSnapshot) => {
    const prev = settings;
    setLocal(next);
    setSaving(true);
    try {
      await setSettings(next);
      // Mirror disabled list into the UI store so PipelineStack
      // hides cards immediately on toggle (no need to wait for the
      // next mount to re-fetch).
      setDisabledPipelines(next.disabled_pipelines);
    } catch (e) {
      // Revert on failure so the UI doesn't show a "saved" state
      // that didn't actually persist.
      setLocal(prev);
      showToast(`Couldn't save settings: ${String(e)}`, "error");
    } finally {
      setSaving(false);
    }
  };

  const togglePipeline = (id: string) => {
    if (!settings) return;
    const isDisabled = settings.disabled_pipelines.includes(id);
    void update({
      ...settings,
      disabled_pipelines: isDisabled
        ? settings.disabled_pipelines.filter((p) => p !== id)
        : [...settings.disabled_pipelines, id],
    });
  };

  return (
    <div
      data-testid="settings-panel"
      className="fixed inset-0 z-50 bg-black p-4 overflow-auto text-fg"
    >
      <div className="flex items-center gap-2 mb-3">
        <h1 className="text-base font-semibold">Settings</h1>
        <span className="text-[10px] text-fg-muted">
          {saving ? "saving…" : "changes save automatically"}
        </span>
        <div className="flex-1" />
        <button
          type="button"
          onClick={onClose}
          className="text-xs px-2 py-1 rounded bg-white/10 hover:bg-white/20"
        >
          Close
        </button>
      </div>

      {!settings ? (
        <div className="text-fg-muted text-xs">loading…</div>
      ) : (
        <div className="flex flex-col gap-4">
          <Section title="Pinpoint reasoning">
            <Toggle
              checked={settings.is_nmpz}
              onChange={(v) => update({ ...settings, is_nmpz: v })}
              label="NMPZ rules (No Move / No Pan / No Zoom)"
              help="Tells the LLM ensemble it's working from one frozen frame and adds a continent → country → region → coords reasoning scaffold. Toggle on whenever you're playing an NMPZ-ruleset game (Duels-NMPZ, BR-NMPZ, etc.)."
            />
          </Section>

          <Section title="Game monitoring">
            <Toggle
              checked={settings.auto_detect_enabled}
              onChange={(v) =>
                update({ ...settings, auto_detect_enabled: v })
              }
              label="Auto-detect new rounds"
              help="Periodically hashes the GeoGuessr browser window and notifies on scene changes. ~1% CPU when active."
            />
            <Toggle
              checked={settings.auto_fire_on_new_round}
              onChange={(v) =>
                update({ ...settings, auto_fire_on_new_round: v })
              }
              label="Auto-fire capture on detected scene change"
              help="When a scene change is detected, automatically dispatch Ctrl+Shift+G. Requires auto-detect."
              disabled={!settings.auto_detect_enabled}
            />
            <Toggle
              checked={settings.auto_truth_from_score_screen}
              onChange={(v) =>
                update({ ...settings, auto_truth_from_score_screen: v })
              }
              label="Auto-record truth from GeoGuessr score screen"
              help="On scene change, asks the LLM whether the screen is a results / score screen and extracts the truth coords + country shown there. Auto-attaches to the most recent round in stats. One extra LLM call per scene change when active. Requires auto-detect."
              disabled={!settings.auto_detect_enabled}
            />
          </Section>

          <Section title="Disable pipelines">
            <div className="text-[11px] text-fg-muted mb-1">
              Disabled pipelines are skipped per round — saves API spend
              and removes a slot's noise from the synthesis. The
              reverse-image launcher cannot be disabled (it's the
              zero-cost manual fallback).
            </div>
            {PIPELINES_DISABLEABLE.map((p) => (
              <Toggle
                key={p.id}
                checked={!settings.disabled_pipelines.includes(p.id)}
                onChange={() => togglePipeline(p.id)}
                label={p.label}
                help={p.help}
              />
            ))}
          </Section>

        </div>
      )}
    </div>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded border border-white/10 bg-white/5 p-3">
      <h2 className="text-xs font-semibold mb-2">{title}</h2>
      <div className="flex flex-col gap-2">{children}</div>
    </section>
  );
}

function Toggle({
  checked,
  onChange,
  label,
  help,
  disabled,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  help?: string;
  disabled?: boolean;
}) {
  return (
    <label
      className={`flex flex-col gap-0.5 text-xs ${
        disabled ? "opacity-50" : "cursor-pointer"
      }`}
    >
      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          disabled={disabled}
        />
        <span className="font-semibold">{label}</span>
      </div>
      {help && <span className="text-[11px] text-fg-muted ml-6">{help}</span>}
    </label>
  );
}
